import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface ClosedWonAnimationProps {
  isVisible: boolean;
  onAnimationComplete?: () => void;
  userName?: string;
  dealName?: string;
  companyName?: string;
  amount?: string;
}

interface Streak {
  x: number;
  y: number;
  angle: number;
  curveDrift: number;
  speed: number;
  decel: number;
  maxTravel: number;
  traveled: number;
  lw: number;
  alpha: number;
  delay: number;
  active: boolean;
  dead: boolean;
  points: { x: number; y: number }[];
  vx: number;
  vy: number;
  falling: boolean;
  fallAlpha: number;
  noiseSeed: number;
  fallStart: number;
  fallDuration: number;
}

type Phase = "idle" | "in" | "hold" | "out";

const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);
const easeInCubic = (x: number) => x * x * x;
const easeInOutCubic = (x: number) =>
  x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
const easeInOutQuart = (x: number) =>
  x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2;
const lingerThenDrop = (x: number) => {
  if (x < 0.3) return 1;
  const k = (x - 0.3) / 0.7;
  return 1 - easeInOutCubic(k);
};
const easeOutBackSubtle = (x: number) => {
  const c1 = 1.2;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
};

const PHASE_IN_MS = 2200;
const PHASE_HOLD_MS = 3000;
const PHASE_OUT_MS = 1800;
const BADGE_DELAY_MS = 950;
const BADGE_IN_MS = 1400;
const BADGE_START_Y = 72;

const ClosedWonAnimation = ({
  isVisible,
  onAnimationComplete,
  userName = "",
  dealName = "",
  companyName = "",
  amount = ""
}: ClosedWonAnimationProps) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const onAnimationCompleteRef = useRef(onAnimationComplete);
  onAnimationCompleteRef.current = onAnimationComplete;

  const stateRef = useRef({
    progress: 0,
    animating: false,
    globalFade: 1,
    t: 0,
    streaks: [] as Streak[],
    W: 0,
    H: 0,
    phase: "idle" as Phase,
    animStartMs: 0,
    holdStartMs: 0,
    outStartMs: 0,
    lastT: 0
  });

  const makeStreak = useCallback((delay: number = 0): Streak => {
    const { W, H } = stateRef.current;
    const side = Math.random() < 0.5 ? -1 : 1;
    const initTilt = Math.random() * 0.6 * side;
    return {
      x: W / 2 + (Math.random() - 0.5) * W * 0.06,
      y: H + 5,
      angle: -Math.PI / 2 + initTilt,
      curveDrift: (Math.random() * 0.012 + 0.0025) * side,
      speed: Math.random() * 3.4 + 2.8,
      decel: Math.random() * 0.0024 + 0.0014,
      maxTravel: H * (Math.random() * 0.55 + 0.55),
      traveled: 0,
      lw: Math.random() * 1.1 + 0.25,
      alpha: Math.random() * 0.7 + 0.15,
      delay,
      active: false,
      dead: false,
      points: [],
      vx: 0,
      vy: 0,
      falling: false,
      fallAlpha: 1,
      noiseSeed: Math.random() * 1000,
      fallStart: 0,
      fallDuration: 110
    };
  }, []);

  const updateStreak = useCallback((s: Streak): void => {
    const { H } = stateRef.current;

    if (s.delay > 0) {
      s.delay--;
      return;
    }
    s.active = true;

    if (s.falling) {
      s.vy += 0.018;
      s.vx *= 0.999;
      s.x += s.vx;
      s.y += s.vy;
      s.fallStart++;
      const fk = Math.min(1, s.fallStart / s.fallDuration);
      s.fallAlpha = 1 - easeInCubic(fk);
      s.points.push({ x: s.x, y: s.y });
      if (s.points.length > 38) s.points.shift();
      if (s.fallAlpha <= 0 || s.y > H + 40) s.dead = true;
      return;
    }

    s.speed = Math.max(0, s.speed - s.decel * s.speed);
    const wobble = 1 + Math.sin(s.noiseSeed + s.traveled * 0.04) * 0.06;
    const stepSpeed = s.speed * wobble;
    const pf = s.traveled / s.maxTravel;
    s.angle += s.curveDrift * (1 + pf * 0.6);
    s.x += Math.cos(s.angle) * stepSpeed;
    s.y += Math.sin(s.angle) * stepSpeed;
    s.points.push({ x: s.x, y: s.y });
    if (s.points.length > 38) s.points.shift();
    s.traveled += stepSpeed;

    if (s.speed < 0.5 || s.traveled >= s.maxTravel) {
      s.falling = true;
      s.vx = Math.cos(s.angle) * s.speed * 0.4;
      s.vy = Math.sin(s.angle) * s.speed * 0.4;
    }
  }, []);

  const drawStreak = useCallback(
    (ctx: CanvasRenderingContext2D, s: Streak): void => {
      const { globalFade } = stateRef.current;
      if (!s.active || s.points.length < 2) return;

      const baseAlpha = s.falling
        ? s.alpha * s.fallAlpha * globalFade
        : s.alpha * Math.min(1, s.speed / 6) * globalFade;
      if (baseAlpha < 0.01) return;

      const pts = s.points;
      const tip = pts[pts.length - 1];
      const tail = pts[0];

      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length - 1; i++) {
        const mx = (pts[i].x + pts[i + 1].x) * 0.5;
        const my = (pts[i].y + pts[i + 1].y) * 0.5;
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
      }
      ctx.lineTo(tip.x, tip.y);

      const gh = ctx.createLinearGradient(tail.x, tail.y, tip.x, tip.y);
      gh.addColorStop(0, "rgba(120,180,255,0)");
      gh.addColorStop(0.5, `rgba(120,180,255,${baseAlpha * 0.08})`);
      gh.addColorStop(1, `rgba(160,200,255,${baseAlpha * 0.18})`);
      ctx.strokeStyle = gh;
      ctx.lineWidth = s.lw * 4.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();

      const g = ctx.createLinearGradient(tail.x, tail.y, tip.x, tip.y);
      g.addColorStop(0, "rgba(255,255,255,0)");
      g.addColorStop(0.35, `rgba(255,255,255,${baseAlpha * 0.2})`);
      g.addColorStop(0.8, `rgba(255,255,255,${baseAlpha * 0.75})`);
      g.addColorStop(1, `rgba(255,255,255,${baseAlpha})`);
      ctx.strokeStyle = g;
      ctx.lineWidth = s.lw;
      ctx.stroke();

      const gw = ctx.createRadialGradient(
        tip.x,
        tip.y,
        0,
        tip.x,
        tip.y,
        s.lw * 8
      );
      gw.addColorStop(0, `rgba(255,255,255,${baseAlpha * 0.55})`);
      gw.addColorStop(0.5, `rgba(180,210,255,${baseAlpha * 0.25})`);
      gw.addColorStop(1, "rgba(0,0,0,0)");
      ctx.beginPath();
      ctx.arc(tip.x, tip.y, s.lw * 8, 0, Math.PI * 2);
      ctx.fillStyle = gw;
      ctx.fill();
    },
    []
  );

  const drawBackground = useCallback((ctx: CanvasRenderingContext2D): void => {
    const { W, H, progress, globalFade, t: tt } = stateRef.current;
    ctx.clearRect(0, 0, W, H);
    if (progress <= 0) return;

    const sinkOffset = (1 - globalFade) * H * 0.35;
    const p = globalFade * progress;

    const backdrop = ctx.createLinearGradient(
      0,
      H + sinkOffset + H * 0.1,
      0,
      H + sinkOffset - H * 1.1
    );
    backdrop.addColorStop(0, `rgba(18,25,38,${1.0 * p})`);
    backdrop.addColorStop(0.22, `rgba(18,25,38,${0.95 * p})`);
    backdrop.addColorStop(0.45, `rgba(18,25,38,${0.78 * p})`);
    backdrop.addColorStop(0.65, `rgba(18,25,38,${0.5 * p})`);
    backdrop.addColorStop(0.85, `rgba(18,25,38,${0.2 * p})`);
    backdrop.addColorStop(1, "rgba(18,25,38,0)");
    ctx.fillStyle = backdrop;
    ctx.fillRect(0, 0, W, H);

    const baseG = ctx.createLinearGradient(
      0,
      H + sinkOffset + H * 0.15,
      0,
      H + sinkOffset - H * 0.85
    );
    baseG.addColorStop(0, `rgba(0,40,255,${0.42 * p})`);
    baseG.addColorStop(0.12, `rgba(0,40,255,${0.4 * p})`);
    baseG.addColorStop(0.28, `rgba(10,30,140,${0.32 * p})`);
    baseG.addColorStop(0.48, `rgba(10,20,80,${0.18 * p})`);
    baseG.addColorStop(0.7, `rgba(10,20,60,${0.07 * p})`);
    baseG.addColorStop(1, "rgba(0,0,40,0)");
    ctx.fillStyle = baseG;
    ctx.fillRect(0, 0, W, H);

    const pool1x = W * 0.5 + Math.sin(tt * 0.6) * W * 0.06;
    const pool1y = H * 0.92 + sinkOffset * 0.3 + Math.cos(tt * 0.5) * H * 0.03;
    const pool1r = Math.max(W, H) * 0.85;
    const pool1 = ctx.createRadialGradient(
      pool1x,
      pool1y,
      0,
      pool1x,
      pool1y,
      pool1r
    );
    pool1.addColorStop(0, `rgba(0,64,255,${0.35 * p})`);
    pool1.addColorStop(0.18, `rgba(0,64,255,${0.22 * p})`);
    pool1.addColorStop(0.42, `rgba(10,30,140,${0.1 * p})`);
    pool1.addColorStop(0.72, `rgba(10,20,60,${0.03 * p})`);
    pool1.addColorStop(1, "rgba(0,0,40,0)");
    ctx.fillStyle = pool1;
    ctx.fillRect(0, 0, W, H);

    const pool2x = W * 0.38 + Math.cos(tt * 0.7) * W * 0.07;
    const pool2y = H * 1.05 + sinkOffset * 0.2 + Math.sin(tt * 0.4) * H * 0.025;
    const pool2r = Math.max(W, H) * 0.55;
    const pool2 = ctx.createRadialGradient(
      pool2x,
      pool2y,
      0,
      pool2x,
      pool2y,
      pool2r
    );
    pool2.addColorStop(0, `rgba(51,187,255,${0.18 * p})`);
    pool2.addColorStop(0.3, `rgba(30,90,200,${0.08 * p})`);
    pool2.addColorStop(0.65, `rgba(10,30,100,${0.02 * p})`);
    pool2.addColorStop(1, "rgba(0,0,40,0)");
    ctx.fillStyle = pool2;
    ctx.fillRect(0, 0, W, H);

    const pool3x = W * 0.65 + Math.sin(tt * 0.45 + 1.5) * W * 0.05;
    const pool3y = H * 1.0 + sinkOffset * 0.25;
    const pool3r = Math.max(W, H) * 0.45;
    const pool3 = ctx.createRadialGradient(
      pool3x,
      pool3y,
      0,
      pool3x,
      pool3y,
      pool3r
    );
    pool3.addColorStop(0, `rgba(0,64,255,${0.2 * p})`);
    pool3.addColorStop(0.4, `rgba(10,30,120,${0.06 * p})`);
    pool3.addColorStop(1, "rgba(0,0,40,0)");
    ctx.fillStyle = pool3;
    ctx.fillRect(0, 0, W, H);
  }, []);

  const triggerAnimation = useCallback(() => {
    const state = stateRef.current;
    const { W, H } = state;

    state.animating = true;
    state.progress = 0;
    state.globalFade = 1;
    state.streaks = [];
    state.phase = "in";
    state.animStartMs = performance.now();

    const badge = badgeRef.current;
    if (badge) {
      badge.style.opacity = "0";
      badge.style.transform = `translateY(${BADGE_START_Y}px)`;
    }

    const gauss = () => {
      const u1 = Math.random() || 1e-9;
      const u2 = Math.random();
      return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    };
    const clampedGauss = (sigma = 1) =>
      Math.max(-2.5, Math.min(2.5, gauss())) * sigma;

    const total = 72;
    for (let i = 0; i < total; i++) {
      const s = makeStreak(Math.floor(Math.random() * 60));
      const xOffset = clampedGauss(W * 0.05);
      s.x = W / 2 + xOffset;
      const fanFactor = (xOffset / W) * 1.8;
      s.angle = -Math.PI / 2 + fanFactor + (Math.random() - 0.5) * 0.12;
      const driftSign = xOffset >= 0 ? 1 : -1;
      s.curveDrift = (Math.random() * 0.012 + 0.002) * driftSign;
      const centerness = 1 - Math.min(1, Math.abs(xOffset) / (W * 0.1));
      s.lw = (Math.random() * 1.0 + 0.3) * (0.85 + centerness * 0.4);
      s.alpha = (Math.random() * 0.55 + 0.2) * (0.85 + centerness * 0.3);
      s.maxTravel = H * (Math.random() * 0.45 + 0.5 + centerness * 0.15);
      state.streaks.push(s);
    }

    for (let i = 0; i < 6; i++) {
      const s = makeStreak(Math.floor(Math.random() * 40) + 25);
      s.x = W / 2 + clampedGauss(W * 0.018);
      s.angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.06;
      s.curveDrift = (Math.random() - 0.5) * 0.004;
      s.lw = Math.random() * 1.3 + 0.7;
      s.alpha = 0.5;
      s.maxTravel = H * (Math.random() * 0.15 + 0.7);
      state.streaks.push(s);
    }
  }, [makeStreak]);

  const loop = useCallback(
    (ts?: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const state = stateRef.current;
      const now = ts ?? performance.now();
      if (!state.lastT) state.lastT = now;
      const dt = Math.min(50, now - state.lastT);
      state.lastT = now;
      state.t += dt * 0.0004;

      if (state.animating) {
        const elapsed = now - state.animStartMs;
        const badge = badgeRef.current;

        if (badge && state.phase !== "out") {
          const badgeElapsed = elapsed - BADGE_DELAY_MS;
          if (badgeElapsed > 0) {
            const k = Math.min(1, badgeElapsed / BADGE_IN_MS);
            const settleProgress = easeOutBackSubtle(k);
            const yPos = BADGE_START_Y * (1 - settleProgress);
            const op = easeOutCubic(
              Math.min(1, badgeElapsed / (BADGE_IN_MS * 0.85))
            );
            let finalY = yPos;
            if (k >= 1) {
              const breathePhase = (badgeElapsed - BADGE_IN_MS) / 1000;
              finalY = Math.sin(breathePhase * 0.9) * 1.2;
            }
            badge.style.opacity = String(op);
            badge.style.transform = `translateY(${finalY}px)`;
          }
        }

        if (state.phase === "in") {
          const k = Math.min(1, elapsed / PHASE_IN_MS);
          state.progress = easeInOutCubic(k);
          if (k >= 1) {
            state.phase = "hold";
            state.holdStartMs = now;
            state.progress = 1;
          }
        } else if (state.phase === "hold") {
          state.progress = 1;
          if (now - state.holdStartMs >= PHASE_HOLD_MS) {
            state.phase = "out";
            state.outStartMs = now;
          }
        } else if (state.phase === "out") {
          const k = Math.min(1, (now - state.outStartMs) / PHASE_OUT_MS);
          state.globalFade = 1 - easeInOutQuart(k);
          if (badge) {
            const lingerK = lingerThenDrop(k);
            const dropProgress = 1 - lingerK;
            const exitElapsed = (now - state.outStartMs) / 1000;
            const float =
              Math.sin((exitElapsed + PHASE_HOLD_MS / 1000) * 0.9) *
              1.2 *
              lingerK;
            const badgeDrop = float + easeInCubic(dropProgress) * 40;
            badge.style.opacity = String(lingerK);
            badge.style.transform = `translateY(${badgeDrop}px)`;
          }
          if (k >= 1) {
            state.animating = false;
            state.phase = "idle";
            state.progress = 0;
            state.globalFade = 1;
            state.streaks = [];
            if (badge) {
              badge.style.opacity = "";
              badge.style.transform = "";
            }
            onAnimationCompleteRef.current?.();
            return;
          }
        }
      }

      drawBackground(ctx);

      ctx.globalCompositeOperation = "lighter";
      state.streaks = state.streaks.filter((s) => {
        updateStreak(s);
        drawStreak(ctx, s);
        return !s.dead;
      });
      ctx.globalCompositeOperation = "source-over";

      animationRef.current = requestAnimationFrame(loop);
    },
    [drawBackground, updateStreak, drawStreak]
  );

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    stateRef.current.W = rect.width;
    stateRef.current.H = rect.height;
    canvas.width = rect.width;
    canvas.height = rect.height;
  }, []);

  useEffect(() => {
    if (isVisible) {
      resize();
      window.addEventListener("resize", resize);
      stateRef.current.lastT = 0;
      triggerAnimation();
      animationRef.current = requestAnimationFrame(loop);

      return () => {
        window.removeEventListener("resize", resize);
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  if (!isVisible) return <></>;

  const displayDealInfo =
    companyName && dealName
      ? `${companyName} — ${dealName}`
      : companyName || dealName || "";

  const congratsText = userName
    ? t("deals.closedWonAnimation.congratulations", { userName })
    : t("deals.closedWonAnimation.congratulationsGeneric");

  const closedWonText = t("deals.closedWonAnimation.closedWon");

  return (
    <div
      className="absolute inset-0 z-[9999] overflow-hidden pointer-events-none"
      style={{
        fontFamily: "'Figtree', system-ui, sans-serif"
      }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ width: "100%", height: "100%" }}
      />
      <div
        className="absolute inset-0 flex flex-col items-center justify-center z-10"
        style={{ paddingTop: "38vh" }}
      >
        <div
          ref={badgeRef}
          className="text-center will-change-[opacity,transform]"
          style={{ opacity: 0, transform: `translateY(${BADGE_START_Y}px)` }}
        >
          <div
            className="text-white/50 uppercase tracking-[0.08em] font-normal mb-2.5"
            style={{ fontSize: "clamp(12px, 1.4vw, 18px)" }}
          >
            {congratsText}
          </div>
          <div
            className="text-white font-semibold tracking-[-0.03em] mb-2.5 leading-tight"
            style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}
          >
            {closedWonText}
          </div>
          {displayDealInfo && (
            <div
              className="text-white/40 font-normal mb-2.5"
              style={{ fontSize: "clamp(12px, 1.4vw, 18px)" }}
            >
              {displayDealInfo}
            </div>
          )}
          {amount && (
            <div
              className="text-white/75 font-semibold mt-[18px]"
              style={{ fontSize: "clamp(16px, 2vw, 24px)" }}
            >
              {amount}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClosedWonAnimation;
